/** sessionStorage key for tab-scoped auth_as (not shared across tabs). */
export const TAB_AUTH_AS_STORAGE_KEY = 'app.admin.tab-auth-as';

/** Query param used by backoffice deep links to impersonate a user in this tab only. */
export const AUTH_AS_QUERY_PARAM = 'auth_as';

/**
 * Returns the tab-scoped auth_as user id from sessionStorage, if any.
 */
export function getTabAuthAs(): string | null {
  try {
    return sessionStorage.getItem(TAB_AUTH_AS_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Persists a tab-scoped auth_as user id in sessionStorage.
 */
export function setTabAuthAs(userId: string): void {
  try {
    sessionStorage.setItem(TAB_AUTH_AS_STORAGE_KEY, userId);
  } catch {
    // Ignore quota / private-mode failures; API calls simply won't carry auth_as.
  }
}

/**
 * Clears the tab-scoped auth_as override (e.g. on logout).
 */
export function clearTabAuthAs(): void {
  try {
    sessionStorage.removeItem(TAB_AUTH_AS_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

/**
 * Reads `auth_as` from a query string, stores it in sessionStorage, and returns it.
 * Does not mutate the URL — callers should strip the param via the router.
 */
export function captureTabAuthAsFromSearch(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const fromUrl = params.get(AUTH_AS_QUERY_PARAM);
  if (!fromUrl) {
    return getTabAuthAs();
  }
  setTabAuthAs(fromUrl);
  return fromUrl;
}

/**
 * Builds a search string with `auth_as` removed. Returns '' when no params remain.
 */
export function stripAuthAsFromSearch(search: string): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (!params.has(AUTH_AS_QUERY_PARAM)) {
    return search.startsWith('?') ? search : search ? `?${search}` : '';
  }
  params.delete(AUTH_AS_QUERY_PARAM);
  const next = params.toString();
  return next ? `?${next}` : '';
}
